'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import clsx from 'clsx';
import {
  ChevronDownIcon,
  HomeIcon,
  BookIcon,
  UserIcon,
  BalanceScaleIcon,
  EmailIcon,
  StarIcon,
  LawBookIcon,
} from '@/components/icons';
import dynamic from 'next/dynamic';

import type {
  NavItem,
  MoreItem,
} from '@/components/layout/types';

interface HeaderClientProps {
  navItems: NavItem[];
  moreItems: MoreItem[];
}

/**
 * HeaderClient - Client Component untuk bagian interaktif header
 *
 * Komponen ini menangani semua interaktivitas header seperti:
 * - State scrolling
 * - Toggle menu mobile
 * - Dropdown menu
 * - Event handlers
 */


const iconMap = {
  HomeIcon,
  BookIcon,
  UserIcon,
  BalanceScaleIcon,
  EmailIcon,
  StarIcon,
  LawBookIcon,
};



const MoreDropdown = dynamic(() => import('./MoreDropdown'));

export default function HeaderClient({
  navItems,
  moreItems,
}: HeaderClientProps) {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();

  // Detect article pages: any path that starts with /blog/ and has at least two
  // additional segments (e.g., /blog/category/slug or deeper)
  const isArticlePage =
    pathname.startsWith('/blog/') &&
    pathname.split('/').filter(Boolean).length >= 3;

  // Header stays visible in mobile to allow closing menu
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 0);
      if (isMenuOpen) {
        setIsMenuOpen(false);
      }
    };

    // Attach scroll listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup on unmount or deps change
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMenuOpen]);
  

  return (
    <>
      {/* Desktop Header */}
      <header
        role="banner"
        aria-label="Main navigation"
        className={clsx(
          'fixed top-6 left-1/2 -translate-x-1/2 z-50 h-12 flex items-center justify-between',
          'hidden md:flex px-4 md:px-0 transition-all duration-300',
          scrolled && 'rounded-lg backdrop-blur-sm bg-[#1A1A1A]/90 shadow-lg',
          scrolled && 'sm:w-[460px]',
          !scrolled && 'sm:w-full'
        )}
      >
        <div className="flex items-center justify-between h-full w-full">
          {/* Logo - Legal styled */}
          <Link
            href="/"
            prefetch={true}
            className="flex items-center gap-2 text-sm hover:text-emerald-400 transition-colors relative "
          ></Link>

          {/* Desktop Navigation - Legal styled */}
          <nav
            className="hidden md:flex items-center gap-1 "
            aria-label="Primary navigation"
          >
            {navItems.map((item: NavItem) => {
              const isActive = pathname === item.href;
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  prefetch={true}
                  className={clsx(
                    'relative inline-flex items-center gap-1.5 py-2 mx-4 group',
                    'text-[0.95rem]',
                    'transition-all duration-300',
                    isActive ? 'text-neutral-300 ' : 'text-neutral-300 '
                  )}
                >
                  <span
                    className={clsx(
                      'relative',
                      'before:absolute before:bottom-0 before:left-0 before:w-0 before:h-0.5',
                      'before:bg-gradient-to-r before:from-neutral-400 before:to-neutral-300',
                      'before:transition-all before:duration-300',
                      'group-hover:before:w-full',
                      isActive && 'before:w-full'
                    )}
                  >
                    {item.label}
                  </span>
                </Link>
              );
            })}
            <MoreDropdown items={moreItems} />
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3 pr-4">
            {/* Menu button handled outside for mobile */}
          </div>
        </div>
      </header>

      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className={clsx(
          'fixed top-6 right-4 md:hidden inline-flex items-center gap-2 px-4 py-2 rounded-lg mt-8',
          'bg-[#1A1A1A]/50 border border-white/10 backdrop-blur-sm',
          'text-neutral-400 text-sm font-medium shadow-lg',
          'transition-all duration-200 hover:bg-gradient-to-br hover:from-[#222]/90 hover:to-[#1A1A1A]/90 z-50',
          ''
        )}
        aria-label="Toggle menu"
      >
        <span>{isMenuOpen ? 'Close' : 'Menu'}</span>
        <ChevronDownIcon
          className={clsx(
            'w-3 h-3 transition-transform duration-200',
            isMenuOpen && 'rotate-180'
          )}
          strokeWidth={2}
        />
      </button>

      {/* Mobile Menu - Legal styled */}
      {isMenuOpen && (
        <nav
          className="fixed inset-0 z-40 md:hidden bg-gradient-to-b from-black/90 via-black/85 to-black/90 backdrop-blur-xl pt-24 border-t border-neutral-800/50"
          aria-label="Mobile navigation"
        >
          <div className="container mx-auto px-4 py-2">
            <div className="space-y-1">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  prefetch={true}
                  onClick={() => setIsMenuOpen(false)}
                  className={clsx(
                    'block p-4 rounded-lg border text-sm',
                    'transition-all duration-300 group relative overflow-hidden',
                    pathname === item.href
                      ? 'text-emerald-400 border-neutral-800 '
                      : 'text-gray-300 bg-[#0a0a0a]/80 border border-white/5 hover:text-emerald-400 hover:bg-gradient-to-r hover:from-[#222]/60 hover:to-[#1A1A1A]/60 hover:border-emerald-500/20'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-[#1A1A1A]/70 border border-white/5 mt-0.5">
                      {React.createElement(iconMap[item.icon as keyof typeof iconMap], {
                        className: 'w-4 h-4 text-neutral-400 transition-all duration-200 group-hover:scale-110',
                        strokeWidth: 1.5,
                      })}
                    </div>
                    <div className="flex flex-col gap-0.">
                      <span className="font-medium text-sm leading">
                        {item.label}
                      </span>
                      <span className="text-xs text-neutral-400 leading">
                        {item.description}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}

              {/* Menggabungkan moreItems ke dalam daftar utama tanpa pemisah */}
              {moreItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  prefetch={true}
                  onClick={() => setIsMenuOpen(false)}
                  className={clsx(
                    'block p-4 rounded-lg border text-sm',
                    'transition-all duration-300 group relative overflow-hidden',
                    pathname === item.href
                      ? 'text-emerald-400 border-neutral-800 '
                      : 'text-gray-300 bg-[#0a0a0a]/80 border border-white/5 hover:text-emerald-400 hover:bg-gradient-to-r hover:from-[#222]/60 hover:to-[#1A1A1A]/60 hover:border-emerald-500/20'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center w-8 h-8 rounded-md bg-[#1A1A1A]/70 border border-white/5 mt-0.5">
                      {React.createElement(iconMap[item.icon as keyof typeof iconMap], {
                        className: 'w-4 h-4 text-neutral-400 transition-all duration-200',
                        strokeWidth: 1.5,
                      })}
                    </div>
                    <div className="flex flex-col gap-0.">
                      <span className="font-medium text-sm leading">
                        {item.label}
                      </span>
                      <span className="text-xs text-neutral-400 leading">
                        {item.description}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </nav>
      )}
    </>
  );
}
